import { ErrorHandler, Injectable, inject } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { catchError, take, timeout } from 'rxjs/operators';
import { RouterService } from '@core/router/router.service';

/**
 * How long a route may wait for the store value it needs. Generous — a cold backend is slow,
 * but not silent-forever slow.
 */
export const ROUTE_DATA_TIMEOUT_MS = 15_000;

/**
 * Every resolver here waits on an Akita `select…WhenTruthy$`, which emits the *first* usable
 * value and otherwise waits forever. Anything that stops that value from arriving — a failed
 * `/private/*` call, an access token that never resolves, an account without a team — used to
 * leave the router waiting indefinitely: the route was never activated, the app rendered a
 * blank page, and nothing reached the console to explain it.
 *
 * Route data is loaded through here so that failure mode has exactly one implementation.
 */
@Injectable({ providedIn: 'root' })
export class RouteDataLoader {
  private routerService = inject(RouterService);
  private errorHandler = inject(ErrorHandler);

  /**
   * Emit the first value of `source$`, or give up after `timeoutMs`: report the failure to the
   * app's {@link ErrorHandler} (Rollbar in production) and send the user to the load-failed
   * page. Completing empty cancels the pending navigation, so that redirect wins.
   *
   * `take(1)` keeps the timeout from outliving the value: the router only ever uses the first
   * emission, whether or not the source completes by itself.
   */
  awaitOrFail<T>(source$: Observable<T>, timeoutMs = ROUTE_DATA_TIMEOUT_MS): Observable<T> {
    return source$.pipe(
      take(1),
      timeout(timeoutMs),
      catchError((err) => {
        this.errorHandler.handleError(err);
        this.routerService.navigateToLoadFailed();
        return EMPTY;
      }),
    );
  }
}
